// src/components/Separation/ResignationList.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { ResignationRequest, ResignationAction } from '../../types/separation';
import { getResignations, actionResignation, withdrawResignation, earlyReleaseAction } from '../../api/separation';
import ResignationModal from './resignationmodal';

interface ResignationListProps {
  onViewChecklist?: (type: string, id: string) => void;
  onCreateSettlement?: (type: string, id: string, employeeId: string) => void;
  userRole?: string;
}

const ResignationList: React.FC<ResignationListProps> = ({ onViewChecklist, onCreateSettlement, userRole = 'admin' }) => {
  const [resignations, setResignations] = useState<ResignationRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedResignation, setSelectedResignation] = useState<ResignationRequest | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'action' | 'withdraw' | 'earlyRelease'>('action');
  const limit = 20;

  const loadResignations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getResignations({ page, limit });
      setResignations(response.data || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      console.error('Failed to load resignations:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadResignations();
  }, [loadResignations]);

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      MANAGER_APPROVED: 'bg-blue-100 text-blue-800',
      MANAGER_REJECTED: 'bg-red-100 text-red-800',
      HR_APPROVED: 'bg-purple-100 text-purple-800',
      HR_REJECTED: 'bg-red-100 text-red-800',
      ADMIN_APPROVED: 'bg-green-100 text-green-800',
      ADMIN_REJECTED: 'bg-red-100 text-red-800',
      COMPLETED: 'bg-green-100 text-green-800',
      WITHDRAWN: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const canAction = (status: string): boolean => {
    if (userRole === 'manager') return status === 'PENDING';
    if (userRole === 'hr') return status === 'MANAGER_APPROVED';
    if (userRole === 'admin') return status === 'HR_APPROVED';
    return false;
  };

  const canWithdraw = (status: string): boolean => {
    return !['COMPLETED', 'ADMIN_APPROVED', 'WITHDRAWN'].includes(status);
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && page === 1) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Resignation Requests</h3>
          <p className="text-sm text-gray-500 mt-1">Total: {total}</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resignation Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Working Day</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Early Release</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {resignations.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">Employee ID: {r.employee_id}</div>
                    <div className="text-xs text-gray-500">ID: {r.id.slice(0, 8)}...</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {r.resignation_date ? new Date(r.resignation_date).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {r.requested_last_day ? new Date(r.requested_last_day).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(r.status)}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {r.early_release_requested ? (
                      <span className={`px-2 py-1 text-xs rounded-full ${r.early_release_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {r.early_release_approved ? 'Approved' : 'Requested'}
                      </span>
                    ) : '-'}
                   </td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    {canAction(r.status) && (
                      <button
                        onClick={() => {
                          setSelectedResignation(r);
                          setModalType('action');
                          setModalOpen(true);
                        }}
                        className="text-green-600 hover:text-green-800"
                      >
                        Review
                      </button>
                    )}
                    {canWithdraw(r.status) && (
                      <button
                        onClick={() => {
                          setSelectedResignation(r);
                          setModalType('withdraw');
                          setModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        Withdraw
                      </button>
                    )}
                    <button
                      onClick={() => onViewChecklist?.('RESIGNATION', r.id)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Checklist
                    </button>
                    {r.status === 'ADMIN_APPROVED' && (
                      <button
                        onClick={() => onCreateSettlement?.('RESIGNATION', r.id, r.employee_id)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        Settlement
                      </button>
                    )}
                  </td>
                 </tr>
              ))}
              {resignations.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No resignation requests found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <ResignationModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedResignation(null);
        }}
        resignation={selectedResignation}
        modalType={modalType}
        userRole={userRole}
        onSuccess={loadResignations}
      />
    </>
  );
};

export default ResignationList;